import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Typography } from 'antd';
import styled from 'styled-components';

import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { useAppSelector } from 'store/hook';
import { getCommunityById } from 'utils/helpers/communityMap';

const Container = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: center;
  align-items: center;
  padding: 28px 0 19px;
  border-bottom: solid 1px var(--line-gray);
`;

const CommunityColumn = styled.div<{ align: 'start' | 'end' }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 300px;
  max-width: 300px;
  align-items: ${(props) => (props.align === 'end' ? 'flex-end' : 'flex-start')};
`;

const StyledCommunityName = styled.div<{ align: 'start' | 'end' }>`
  font-size: 20px;
  font-family: var(--font-bold);
  text-align: ${(props) => (props.align === 'end' ? 'end' : 'start')};
  width: 300px;
  max-width: 300px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: inline-block;
`;

const StyledCommunityType = styled(Typography.Text)`
  font-size: 12px;
  line-height: 1.2;
  color: var(--text-secondary);
  margin: 0;
`;

const CommunityMetaRow = styled.div`
  display: flex;
  align-items: center;
  min-height: 16px;
  margin: 0;
`;

const OvalCopy = styled.span`
  width: 3px;
  height: 3px;
  margin: 0 10px;
  border-radius: 50%;
  background-color: var(--text-secondary);
`;

const StyledViewDetails = styled(Typography.Text)`
  font-size: 12px;
  line-height: 1.2;
  font-family: var(--font-bold);
  font-weight: 500;
  color: var(--text-primary);
  margin: 0;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const StyledDivider = styled(Typography.Text)`
  font-size: 14px;
  font-weight: 500;
  font-stretch: normal;
  font-style: oblique;
  line-height: normal;
  letter-spacing: normal;
  text-align: center;
  color: var(--text-secondary);
  padding: 2px 30px 0;
`;

type Props = {
  competitorId: number | null;
};

const EditSwotDataModalHeader = ({ competitorId }: Props) => {
  const { selectedCommunity, communityMap } = useAppSelector((state) => state.global);
  const navigate = useNavigate();

  const { community } = useCommunityViewContext();
  const competitor = getCommunityById(communityMap, competitorId);
  const competitorName = competitor?.comunity_name ?? selectedCommunity?.name ?? 'Unknown Community';
  const communityName = community?.comunity_name ?? 'Unknown Community';
  const communityProfilePath = community?.id ? `/community/${community.id}/info` : null;
  const competitorProfilePath = competitor?.id ? `/competitor/${competitor.id}/info` : null;

  return (
    <Container>
      <CommunityColumn align="end">
        <StyledCommunityName title={communityName} align="end">
          {communityName}
        </StyledCommunityName>
        <CommunityMetaRow>
          <StyledCommunityType>My community</StyledCommunityType>
          <OvalCopy />
          <StyledViewDetails
            onClick={() => {
              if (communityProfilePath) {
                navigate(communityProfilePath);
              }
            }}
          >
            View Details
          </StyledViewDetails>
        </CommunityMetaRow>
      </CommunityColumn>
      <StyledDivider>vs.</StyledDivider>
      <CommunityColumn align="start">
        <StyledCommunityName title={competitorName} align="start">
          {competitorName}
        </StyledCommunityName>
        <CommunityMetaRow>
          <StyledCommunityType>Competitor</StyledCommunityType>
          <OvalCopy />
          <StyledViewDetails
            onClick={() => {
              if (competitorProfilePath) {
                navigate(competitorProfilePath);
              }
            }}
          >
            View Details
          </StyledViewDetails>
        </CommunityMetaRow>
      </CommunityColumn>
    </Container>
  );
};

export default EditSwotDataModalHeader;
